import { useParams, Link, Navigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import {
  ShoppingCart, Zap, Package, Palette, Repeat, CreditCard, Truck, BarChart3,
  Layers, Code, Monitor, PenTool, Globe, Settings, Wrench, Bot, Sparkles,
  MessageSquare, BookOpen, Brain, ArrowRight, ArrowUpRight, CheckCircle2,
  Target, Lightbulb, Users, TrendingUp, Puzzle, Gauge, Shield,
} from "lucide-react";
import { useEffect } from "react";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";

/* ─── Types ─── */
interface ServiceFeature {
  icon: any;
  title: string;
  title_ar: string;
  desc: string;
  desc_ar: string;
}

interface ServiceUseCase {
  title: string;
  title_ar: string;
  desc: string;
  desc_ar: string;
}

interface ServiceData {
  title: string;
  title_ar: string;
  heroDesc: string;
  heroDesc_ar: string;
  icon: any;
  whatWeDo: string;
  whatWeDo_ar: string;
  features: ServiceFeature[];
  useCases: ServiceUseCase[];
  platforms?: string[];
  seoTitle: string;
  seoDesc: string;
}

/* ─── Service Data ─── */
const SERVICES: Record<string, ServiceData> = {
  "shopify-store-design": {
    title: "Shopify Store Design & Development",
    title_ar: "تصميم وتطوير متاجر Shopify",
    heroDesc: "Custom-built Shopify stores designed for conversions, optimized for speed, and ready to scale with your business.",
    heroDesc_ar: "متاجر Shopify مخصصة مصممة للتحويلات ومحسّنة للسرعة وجاهزة للتوسع مع عملك.",
    icon: ShoppingCart,
    whatWeDo: "We design and develop custom Shopify stores from the ground up. From theme customization and product page optimization to checkout flow design and mobile responsiveness, every detail is crafted to convert visitors into customers and support long-term growth.",
    whatWeDo_ar: "نصمم ونطور متاجر Shopify مخصصة من الصفر. من تخصيص القوالب وتحسين صفحات المنتجات إلى تصميم تدفق الدفع والاستجابة للجوال, كل تفصيل مصمم لتحويل الزوار إلى عملاء ودعم النمو طويل المدى.",
    features: [
      { icon: Palette, title: "Custom Theme Design", title_ar: "تصميم قالب مخصص", desc: "Unique, brand-aligned Shopify themes built from scratch.", desc_ar: "قوالب Shopify فريدة ومتوافقة مع العلامة التجارية." },
      { icon: Gauge, title: "Speed Optimization", title_ar: "تحسين السرعة", desc: "Lightning-fast load times for better SEO and conversions.", desc_ar: "أوقات تحميل فائقة السرعة لتحسين SEO والتحويلات." },
      { icon: Monitor, title: "Mobile-First Design", title_ar: "تصميم الجوال أولاً", desc: "Responsive layouts optimized for every screen size.", desc_ar: "تخطيطات متجاوبة محسّنة لجميع أحجام الشاشات." },
      { icon: CreditCard, title: "Checkout Optimization", title_ar: "تحسين الدفع", desc: "Streamlined checkout flows that reduce cart abandonment.", desc_ar: "تدفقات دفع مبسطة تقلل التخلي عن السلة." },
      { icon: Puzzle, title: "App Integrations", title_ar: "تكامل التطبيقات", desc: "Connect with the best Shopify apps for your business.", desc_ar: "اتصل بأفضل تطبيقات Shopify لعملك." },
      { icon: TrendingUp, title: "Conversion Focused", title_ar: "تركيز على التحويل", desc: "Every element designed to drive sales and revenue.", desc_ar: "كل عنصر مصمم لدفع المبيعات والإيرادات." },
    ],
    useCases: [
      { title: "New Brand Launch", title_ar: "إطلاق علامة تجارية جديدة", desc: "Launching a new ecommerce brand and need a professional store.", desc_ar: "إطلاق علامة تجارية جديدة وتحتاج متجراً احترافياً." },
      { title: "Store Redesign", title_ar: "إعادة تصميم المتجر", desc: "Your current store isn't converting and needs a fresh look.", desc_ar: "متجرك الحالي لا يحقق تحويلات ويحتاج مظهراً جديداً." },
      { title: "Scaling Operations", title_ar: "توسيع العمليات", desc: "Growing fast and need a store that can handle the traffic.", desc_ar: "تنمو بسرعة وتحتاج متجراً يتحمل الزيارات العالية." },
    ],
    platforms: ["Shopify", "Shopify Plus"],
    seoTitle: "Shopify Store Design & Development | Wavai",
    seoDesc: "Custom Shopify store design and development. Conversion-focused, speed-optimized ecommerce stores built by Wavai.",
  },
  "shopify-plus": {
    title: "Shopify Plus Solutions",
    title_ar: "حلول Shopify Plus",
    heroDesc: "Enterprise-grade ecommerce for high-growth brands, advanced automations, custom checkouts, and unlimited scalability.",
    heroDesc_ar: "تجارة إلكترونية مؤسسية للعلامات التجارية عالية النمو, أتمتة متقدمة وصفحات دفع مخصصة وقابلية توسع غير محدودة.",
    icon: Zap,
    whatWeDo: "We help enterprise brands unlock the full power of Shopify Plus. From custom checkout experiences and advanced automation with Shopify Flow to multi-currency, multi-language storefronts and B2B wholesale channels, we build solutions that match your ambition.",
    whatWeDo_ar: "نساعد العلامات التجارية المؤسسية على إطلاق القوة الكاملة لـ Shopify Plus. من تجارب الدفع المخصصة والأتمتة المتقدمة إلى واجهات المتاجر متعددة العملات واللغات وقنوات البيع بالجملة.",
    features: [
      { icon: Settings, title: "Custom Checkout", title_ar: "دفع مخصص", desc: "Fully customized checkout experiences with Checkout Extensibility.", desc_ar: "تجارب دفع مخصصة بالكامل مع قابلية التوسع." },
      { icon: Zap, title: "Shopify Flow", title_ar: "أتمتة Shopify Flow", desc: "Advanced workflow automation for operations and marketing.", desc_ar: "أتمتة سير العمل المتقدمة للعمليات والتسويق." },
      { icon: Globe, title: "Multi-Currency & Language", title_ar: "متعدد العملات واللغات", desc: "Sell globally with localized storefronts.", desc_ar: "بيع عالمي مع واجهات متاجر مترجمة." },
      { icon: Users, title: "B2B & Wholesale", title_ar: "B2B والبيع بالجملة", desc: "Dedicated wholesale channels with custom pricing.", desc_ar: "قنوات بيع بالجملة مخصصة مع تسعير خاص." },
      { icon: Shield, title: "Enterprise Security", title_ar: "أمان مؤسسي", desc: "Advanced security features for high-volume stores.", desc_ar: "ميزات أمان متقدمة للمتاجر عالية الحجم." },
      { icon: BarChart3, title: "Advanced Analytics", title_ar: "تحليلات متقدمة", desc: "Deep insights into customer behavior and sales performance.", desc_ar: "رؤى عميقة في سلوك العملاء وأداء المبيعات." },
    ],
    useCases: [
      { title: "High-Volume Stores", title_ar: "متاجر عالية الحجم", desc: "Processing thousands of orders daily and need enterprise reliability.", desc_ar: "معالجة آلاف الطلبات يومياً وتحتاج موثوقية مؤسسية." },
      { title: "Global Expansion", title_ar: "توسع عالمي", desc: "Expanding to new markets with multi-currency and localization.", desc_ar: "التوسع لأسواق جديدة مع تعدد العملات والترجمة." },
      { title: "B2B Commerce", title_ar: "تجارة B2B", desc: "Launching wholesale or B2B channels alongside DTC.", desc_ar: "إطلاق قنوات بيع بالجملة إلى جانب البيع المباشر." },
    ],
    platforms: ["Shopify Plus", "Shopify"],
    seoTitle: "Shopify Plus Solutions | Enterprise Ecommerce | Wavai",
    seoDesc: "Enterprise Shopify Plus development. Custom checkouts, automation, B2B channels, and global expansion for high-growth brands.",
  },
  "woocommerce-development": {
    title: "WooCommerce Development",
    title_ar: "تطوير WooCommerce",
    heroDesc: "Flexible, open-source ecommerce built on WordPress, perfect for custom catalogs, unique requirements, and full ownership.",
    heroDesc_ar: "تجارة إلكترونية مرنة ومفتوحة المصدر على WordPress, مثالية للكتالوجات المخصصة والمتطلبات الفريدة والملكية الكاملة.",
    icon: Package,
    whatWeDo: "We build powerful WooCommerce stores that leverage WordPress's flexibility. From custom product types and advanced filtering to payment gateway integrations and performance optimization, we create stores that are uniquely yours.",
    whatWeDo_ar: "نبني متاجر WooCommerce قوية تستفيد من مرونة WordPress. من أنواع المنتجات المخصصة والتصفية المتقدمة إلى تكامل بوابات الدفع وتحسين الأداء.",
    features: [
      { icon: Code, title: "Custom Development", title_ar: "تطوير مخصص", desc: "Bespoke WooCommerce solutions tailored to your needs.", desc_ar: "حلول WooCommerce مخصصة حسب احتياجاتك." },
      { icon: Puzzle, title: "Plugin Integration", title_ar: "تكامل الإضافات", desc: "Expert integration of essential WooCommerce plugins.", desc_ar: "تكامل احترافي للإضافات الأساسية." },
      { icon: Gauge, title: "Performance Tuning", title_ar: "تحسين الأداء", desc: "Optimized for speed even with large product catalogs.", desc_ar: "محسّن للسرعة حتى مع كتالوجات المنتجات الكبيرة." },
      { icon: CreditCard, title: "Payment Gateways", title_ar: "بوابات الدفع", desc: "Local and international payment provider integrations.", desc_ar: "تكامل مع مزودي الدفع المحليين والدوليين." },
      { icon: Shield, title: "Security Hardening", title_ar: "تعزيز الأمان", desc: "WordPress security best practices for safe transactions.", desc_ar: "أفضل ممارسات أمان WordPress للمعاملات الآمنة." },
      { icon: Wrench, title: "Ongoing Maintenance", title_ar: "صيانة مستمرة", desc: "Regular updates, backups, and performance monitoring.", desc_ar: "تحديثات منتظمة ونسخ احتياطية ومراقبة الأداء." },
    ],
    useCases: [
      { title: "Content-Heavy Ecommerce", title_ar: "تجارة غنية بالمحتوى", desc: "Combining rich content marketing with ecommerce.", desc_ar: "الجمع بين التسويق بالمحتوى الغني والتجارة الإلكترونية." },
      { title: "Custom Product Types", title_ar: "أنواع منتجات مخصصة", desc: "Selling products that need custom configurations.", desc_ar: "بيع منتجات تحتاج إعدادات مخصصة." },
      { title: "Full Ownership", title_ar: "ملكية كاملة", desc: "Businesses wanting complete control over their store.", desc_ar: "شركات تريد سيطرة كاملة على متجرها." },
    ],
    platforms: ["WooCommerce", "WordPress"],
    seoTitle: "WooCommerce Development | Wavai",
    seoDesc: "Custom WooCommerce development on WordPress. Flexible, open-source ecommerce solutions built by Wavai.",
  },
  "ecommerce-ux-optimization": {
    title: "Ecommerce UX Optimization",
    title_ar: "تحسين تجربة المستخدم للمتاجر",
    heroDesc: "Data-driven design improvements that reduce friction, increase conversions, and create delightful shopping experiences.",
    heroDesc_ar: "تحسينات تصميم مبنية على البيانات تقلل الاحتكاك وتزيد التحويلات وتخلق تجارب تسوق مميزة.",
    icon: Palette,
    whatWeDo: "We analyze your store's user experience and implement strategic design improvements. Using heatmaps, session recordings, A/B testing, and conversion data, we identify friction points and optimize every step of the customer journey.",
    whatWeDo_ar: "نحلل تجربة المستخدم في متجرك وننفذ تحسينات تصميم استراتيجية. باستخدام خرائط الحرارة وتسجيلات الجلسات واختبارات A/B وبيانات التحويل، نحدد نقاط الاحتكاك ونحسّن كل خطوة في رحلة العميل.",
    features: [
      { icon: Target, title: "Conversion Audit", title_ar: "تدقيق التحويل", desc: "Comprehensive analysis of your store's conversion funnel.", desc_ar: "تحليل شامل لمسار التحويل في متجرك." },
      { icon: BarChart3, title: "Data Analysis", title_ar: "تحليل البيانات", desc: "Heatmaps, session recordings, and behavioral analytics.", desc_ar: "خرائط حرارية وتسجيلات جلسات وتحليلات سلوكية." },
      { icon: Palette, title: "UI Redesign", title_ar: "إعادة تصميم الواجهة", desc: "Strategic design improvements based on real user data.", desc_ar: "تحسينات تصميم استراتيجية مبنية على بيانات المستخدمين الفعلية." },
      { icon: Gauge, title: "A/B Testing", title_ar: "اختبارات A/B", desc: "Test and validate design changes before full rollout.", desc_ar: "اختبار والتحقق من تغييرات التصميم قبل النشر الكامل." },
      { icon: Monitor, title: "Mobile Optimization", title_ar: "تحسين الجوال", desc: "Ensure seamless shopping on all mobile devices.", desc_ar: "ضمان تسوق سلس على جميع الأجهزة المحمولة." },
      { icon: TrendingUp, title: "Growth Strategy", title_ar: "استراتيجية النمو", desc: "Ongoing optimization roadmap for continuous improvement.", desc_ar: "خارطة طريق تحسين مستمرة للتحسين المتواصل." },
    ],
    useCases: [
      { title: "Low Conversion Rates", title_ar: "معدلات تحويل منخفضة", desc: "Your store gets traffic but doesn't convert visitors.", desc_ar: "متجرك يحصل على زيارات لكن لا يحول الزوار." },
      { title: "High Cart Abandonment", title_ar: "تخلي عالي عن السلة", desc: "Customers add to cart but don't complete purchase.", desc_ar: "العملاء يضيفون للسلة لكن لا يكملون الشراء." },
      { title: "Post-Redesign Audit", title_ar: "تدقيق بعد إعادة التصميم", desc: "Validating a new design's impact on conversions.", desc_ar: "التحقق من تأثير التصميم الجديد على التحويلات." },
    ],
    platforms: ["Shopify", "WooCommerce", "Webflow"],
    seoTitle: "Ecommerce UX Optimization | Wavai",
    seoDesc: "Data-driven ecommerce UX optimization. Increase conversions with strategic design improvements by Wavai.",
  },
  "store-migration": {
    title: "Store Migration",
    title_ar: "ترحيل المتاجر",
    heroDesc: "Seamless migration from any platform to Shopify or WooCommerce, preserving your SEO rankings, customer data, and design integrity.",
    heroDesc_ar: "ترحيل سلس من أي منصة إلى Shopify أو WooCommerce, مع الحفاظ على تصنيفات SEO وبيانات العملاء وسلامة التصميم.",
    icon: Repeat,
    whatWeDo: "We handle the entire migration process end-to-end. From data mapping and product transfer to URL redirects, SEO preservation, and design recreation, we ensure zero downtime and a smooth transition for your customers.",
    whatWeDo_ar: "نتعامل مع عملية الترحيل بالكامل من البداية إلى النهاية. من تعيين البيانات ونقل المنتجات إلى إعادة توجيه الروابط والحفاظ على SEO وإعادة إنشاء التصميم, نضمن عدم توقف الخدمة وانتقال سلس لعملائك.",
    features: [
      { icon: Package, title: "Data Migration", title_ar: "ترحيل البيانات", desc: "Products, customers, orders, and content transferred safely.", desc_ar: "منتجات وعملاء وطلبات ومحتوى يتم نقلها بأمان." },
      { icon: Globe, title: "SEO Preservation", title_ar: "الحفاظ على SEO", desc: "301 redirects and URL mapping to maintain rankings.", desc_ar: "إعادة توجيه 301 وتعيين الروابط للحفاظ على التصنيفات." },
      { icon: Palette, title: "Design Recreation", title_ar: "إعادة إنشاء التصميم", desc: "Your brand identity faithfully recreated on the new platform.", desc_ar: "هوية علامتك التجارية يتم إعادة إنشائها بدقة على المنصة الجديدة." },
      { icon: Shield, title: "Zero Downtime", title_ar: "بدون توقف", desc: "Careful planning ensures no disruption to sales.", desc_ar: "تخطيط دقيق يضمن عدم تعطل المبيعات." },
      { icon: CheckCircle2, title: "Quality Assurance", title_ar: "ضمان الجودة", desc: "Thorough testing before going live on the new platform.", desc_ar: "اختبار شامل قبل الإطلاق على المنصة الجديدة." },
      { icon: Wrench, title: "Post-Migration Support", title_ar: "دعم ما بعد الترحيل", desc: "Ongoing support to ensure everything runs smoothly.", desc_ar: "دعم مستمر لضمان سير كل شيء بسلاسة." },
    ],
    useCases: [
      { title: "Platform Switch", title_ar: "تغيير المنصة", desc: "Moving from Magento, BigCommerce, or custom platforms.", desc_ar: "الانتقال من Magento أو BigCommerce أو منصات مخصصة." },
      { title: "Shopify Migration", title_ar: "الترحيل إلى Shopify", desc: "Consolidating onto Shopify for better scalability.", desc_ar: "التوحيد على Shopify لقابلية توسع أفضل." },
      { title: "Multi-Store Consolidation", title_ar: "توحيد متاجر متعددة", desc: "Merging multiple stores into a single platform.", desc_ar: "دمج متاجر متعددة في منصة واحدة." },
    ],
    platforms: ["Shopify", "WooCommerce", "Magento", "BigCommerce"],
    seoTitle: "Store Migration Services | Wavai",
    seoDesc: "Seamless ecommerce store migration. Move to Shopify or WooCommerce without losing SEO, data, or customers.",
  },
  "payment-integrations": {
    title: "Payment Integrations",
    title_ar: "تكامل بوابات الدفع",
    heroDesc: "Connect with local and global payment providers for a seamless, secure checkout experience that drives conversions.",
    heroDesc_ar: "اتصل بمزودي الدفع المحليين والعالميين لتجربة دفع سلسة وآمنة تزيد التحويلات.",
    icon: CreditCard,
    whatWeDo: "We integrate payment gateways that match your market and customer preferences. From popular local providers like KNET, Benefit, and Mada to global solutions like Stripe, PayPal, and Apple Pay, we ensure a frictionless payment experience.",
    whatWeDo_ar: "ندمج بوابات الدفع التي تتناسب مع سوقك وتفضيلات عملائك. من مزودين محليين مثل KNET وBenefit وMada إلى حلول عالمية مثل Stripe وPayPal وApple Pay.",
    features: [
      { icon: CreditCard, title: "Local Gateways", title_ar: "بوابات محلية", desc: "KNET, Benefit, Mada, and regional payment methods.", desc_ar: "KNET وBenefit وMada وطرق دفع إقليمية." },
      { icon: Globe, title: "Global Providers", title_ar: "مزودون عالميون", desc: "Stripe, PayPal, Apple Pay, and Google Pay.", desc_ar: "Stripe وPayPal وApple Pay وGoogle Pay." },
      { icon: Shield, title: "PCI Compliance", title_ar: "توافق PCI", desc: "Secure payment processing meeting industry standards.", desc_ar: "معالجة دفع آمنة تلبي معايير الصناعة." },
      { icon: Repeat, title: "Multi-Currency", title_ar: "متعدد العملات", desc: "Accept payments in multiple currencies.", desc_ar: "قبول المدفوعات بعملات متعددة." },
    ],
    useCases: [
      { title: "GCC Ecommerce", title_ar: "تجارة دول الخليج", desc: "Need local payment methods for Gulf customers.", desc_ar: "تحتاج طرق دفع محلية لعملاء الخليج." },
      { title: "Global Sales", title_ar: "مبيعات عالمية", desc: "Selling internationally and need multiple payment options.", desc_ar: "بيع دولي وتحتاج خيارات دفع متعددة." },
    ],
    platforms: ["Shopify", "WooCommerce", "Stripe", "PayPal"],
    seoTitle: "Payment Gateway Integrations | Wavai",
    seoDesc: "Payment gateway integration for ecommerce. Local and global payment solutions for seamless checkout experiences.",
  },
  "logistics-fulfillment": {
    title: "Logistics & Fulfillment",
    title_ar: "الخدمات اللوجستية والتنفيذ",
    heroDesc: "Integrate shipping, warehousing, and fulfillment providers to streamline your operations and delight customers.",
    heroDesc_ar: "تكامل مع مزودي الشحن والتخزين والتنفيذ لتبسيط عملياتك وإسعاد العملاء.",
    icon: Truck,
    whatWeDo: "We connect your store with shipping carriers, fulfillment centers, and logistics providers. From automated shipping calculations and label printing to real-time tracking and returns management, we optimize your entire fulfillment pipeline.",
    whatWeDo_ar: "نربط متجرك بشركات الشحن ومراكز التنفيذ ومزودي الخدمات اللوجستية. من حسابات الشحن الآلية وطباعة الملصقات إلى التتبع في الوقت الفعلي وإدارة المرتجعات.",
    features: [
      { icon: Truck, title: "Shipping Integration", title_ar: "تكامل الشحن", desc: "Connect with Aramex, DHL, FedEx, and local carriers.", desc_ar: "اتصل بـ Aramex وDHL وFedEx وشركات الشحن المحلية." },
      { icon: Package, title: "Fulfillment Centers", title_ar: "مراكز التنفيذ", desc: "Third-party fulfillment and warehouse integrations.", desc_ar: "تكامل مع مراكز التنفيذ والمستودعات." },
      { icon: Globe, title: "Real-Time Tracking", title_ar: "تتبع مباشر", desc: "Live shipment tracking for you and your customers.", desc_ar: "تتبع شحنات مباشر لك ولعملائك." },
      { icon: Repeat, title: "Returns Management", title_ar: "إدارة المرتجعات", desc: "Streamlined returns and exchange workflows.", desc_ar: "سير عمل مبسط للمرتجعات والاستبدال." },
    ],
    useCases: [
      { title: "Order Volume Growth", title_ar: "نمو حجم الطلبات", desc: "Scaling operations and need automated fulfillment.", desc_ar: "توسيع العمليات وتحتاج تنفيذ آلي." },
      { title: "Multi-Region Shipping", title_ar: "شحن متعدد المناطق", desc: "Shipping to multiple countries with different carriers.", desc_ar: "شحن لعدة دول مع شركات شحن مختلفة." },
    ],
    platforms: ["Shopify", "WooCommerce", "Aramex", "DHL"],
    seoTitle: "Logistics & Fulfillment Integration | Wavai",
    seoDesc: "Ecommerce logistics and fulfillment integration. Streamline shipping, tracking, and returns management.",
  },
  "ecommerce-analytics": {
    title: "Analytics & Growth",
    title_ar: "التحليلات والنمو",
    heroDesc: "Track performance, understand customer behavior, and continuously optimize for revenue growth.",
    heroDesc_ar: "تتبع الأداء وفهم سلوك العملاء والتحسين المستمر لنمو الإيرادات.",
    icon: BarChart3,
    whatWeDo: "We set up comprehensive analytics, tracking, and reporting for your ecommerce store. From Google Analytics and conversion tracking to custom dashboards and growth strategy, we give you the insights you need to make data-driven decisions.",
    whatWeDo_ar: "نضع تحليلات وتتبع وتقارير شاملة لمتجرك. من Google Analytics وتتبع التحويل إلى لوحات المعلومات المخصصة واستراتيجية النمو, نمنحك الرؤى التي تحتاجها لاتخاذ قرارات مبنية على البيانات.",
    features: [
      { icon: BarChart3, title: "Analytics Setup", title_ar: "إعداد التحليلات", desc: "Google Analytics, Meta Pixel, and conversion tracking.", desc_ar: "Google Analytics وMeta Pixel وتتبع التحويل." },
      { icon: Target, title: "KPI Dashboards", title_ar: "لوحات مؤشرات الأداء", desc: "Custom dashboards for real-time performance monitoring.", desc_ar: "لوحات معلومات مخصصة لمراقبة الأداء في الوقت الفعلي." },
      { icon: TrendingUp, title: "Growth Strategy", title_ar: "استراتيجية النمو", desc: "Data-driven recommendations to scale your revenue.", desc_ar: "توصيات مبنية على البيانات لتوسيع إيراداتك." },
      { icon: Users, title: "Customer Insights", title_ar: "رؤى العملاء", desc: "Understand buying patterns and customer segments.", desc_ar: "فهم أنماط الشراء وشرائح العملاء." },
    ],
    useCases: [
      { title: "Data-Driven Growth", title_ar: "نمو مبني على البيانات", desc: "Want to make decisions based on real performance data.", desc_ar: "تريد اتخاذ قرارات بناءً على بيانات الأداء الفعلية." },
      { title: "Marketing Optimization", title_ar: "تحسين التسويق", desc: "Need to track ROI across marketing channels.", desc_ar: "تحتاج لتتبع العائد على الاستثمار عبر قنوات التسويق." },
    ],
    platforms: ["Google Analytics", "Shopify", "WooCommerce", "Meta"],
    seoTitle: "Ecommerce Analytics & Growth | Wavai",
    seoDesc: "Ecommerce analytics, tracking, and growth optimization. Data-driven strategies to scale your online store.",
  },
  "webflow-development": {
    title: "Webflow Development",
    title_ar: "تطوير مواقع Webflow",
    heroDesc: "Visual-first development with Webflow, responsive, fast, and easy for your team to manage without code.",
    heroDesc_ar: "تطوير بصري أولاً مع Webflow, متجاوب وسريع وسهل الإدارة بدون كود.",
    icon: Layers,
    whatWeDo: "We design and develop premium Webflow websites that combine beautiful design with powerful CMS capabilities. From marketing sites and landing pages to complex multi-page platforms, we leverage Webflow's visual tools to deliver pixel-perfect results your team can manage independently.",
    whatWeDo_ar: "نصمم ونطور مواقع Webflow متميزة تجمع بين التصميم الجميل وقدرات إدارة المحتوى القوية. من مواقع التسويق وصفحات الهبوط إلى المنصات المعقدة متعددة الصفحات.",
    features: [
      { icon: Palette, title: "Custom Design", title_ar: "تصميم مخصص", desc: "Pixel-perfect, brand-aligned visual design.", desc_ar: "تصميم بصري مثالي ومتوافق مع العلامة التجارية." },
      { icon: Layers, title: "CMS Architecture", title_ar: "بنية إدارة المحتوى", desc: "Structured content models for easy management.", desc_ar: "نماذج محتوى منظمة لإدارة سهلة." },
      { icon: Gauge, title: "Performance", title_ar: "الأداء", desc: "Optimized for Core Web Vitals and fast loading.", desc_ar: "محسّن لمؤشرات الويب الأساسية والتحميل السريع." },
      { icon: Puzzle, title: "Integrations", title_ar: "التكاملات", desc: "Connect with HubSpot, Zapier, Airtable, and more.", desc_ar: "اتصل بـ HubSpot وZapier وAirtable والمزيد." },
      { icon: Monitor, title: "Responsive Design", title_ar: "تصميم متجاوب", desc: "Flawless experience across all devices.", desc_ar: "تجربة مثالية عبر جميع الأجهزة." },
      { icon: Globe, title: "Localization", title_ar: "التوطين", desc: "Multi-language support with Webflow Localization.", desc_ar: "دعم متعدد اللغات مع Webflow Localization." },
    ],
    useCases: [
      { title: "Corporate Website", title_ar: "موقع مؤسسي", desc: "Need a professional website your team can update.", desc_ar: "تحتاج موقعاً احترافياً يمكن لفريقك تحديثه." },
      { title: "Marketing Site", title_ar: "موقع تسويقي", desc: "Fast-loading marketing site with CMS capabilities.", desc_ar: "موقع تسويقي سريع التحميل مع قدرات إدارة المحتوى." },
      { title: "Design-First Projects", title_ar: "مشاريع التصميم أولاً", desc: "Projects where design quality is the top priority.", desc_ar: "مشاريع حيث جودة التصميم هي الأولوية القصوى." },
    ],
    platforms: ["Webflow", "Figma"],
    seoTitle: "Webflow Development | Wavai",
    seoDesc: "Professional Webflow development. Custom websites with CMS, animations, and responsive design by Wavai.",
  },
  "wordpress-development": {
    title: "WordPress Development",
    title_ar: "تطوير WordPress",
    heroDesc: "Custom WordPress sites built for performance, security, and content management flexibility.",
    heroDesc_ar: "مواقع WordPress مخصصة مبنية للأداء والأمان ومرونة إدارة المحتوى.",
    icon: Code,
    whatWeDo: "We build custom WordPress solutions, from theme development and plugin customization to headless WordPress implementations and enterprise-grade security. Whether you need a content-rich blog, corporate site, or membership platform, we deliver WordPress at its best.",
    whatWeDo_ar: "نبني حلول WordPress مخصصة, من تطوير القوالب وتخصيص الإضافات إلى تطبيقات WordPress headless والأمان المؤسسي.",
    features: [
      { icon: Code, title: "Custom Themes", title_ar: "قوالب مخصصة", desc: "Bespoke themes built for your brand and needs.", desc_ar: "قوالب مخصصة مبنية لعلامتك التجارية واحتياجاتك." },
      { icon: Puzzle, title: "Plugin Development", title_ar: "تطوير الإضافات", desc: "Custom plugins for unique functionality.", desc_ar: "إضافات مخصصة لوظائف فريدة." },
      { icon: Shield, title: "Security", title_ar: "الأمان", desc: "Hardened WordPress with enterprise security.", desc_ar: "WordPress مُعزّز بأمان مؤسسي." },
      { icon: Gauge, title: "Speed Optimization", title_ar: "تحسين السرعة", desc: "Fast-loading sites with caching and CDN.", desc_ar: "مواقع سريعة التحميل مع التخزين المؤقت وCDN." },
      { icon: Layers, title: "Headless WordPress", title_ar: "WordPress Headless", desc: "Decoupled frontend with WordPress as backend.", desc_ar: "واجهة أمامية منفصلة مع WordPress كخلفية." },
      { icon: Settings, title: "Maintenance", title_ar: "الصيانة", desc: "Ongoing updates, backups, and monitoring.", desc_ar: "تحديثات ونسخ احتياطية ومراقبة مستمرة." },
    ],
    useCases: [
      { title: "Content Platforms", title_ar: "منصات المحتوى", desc: "Blogs, news sites, and content-heavy platforms.", desc_ar: "مدونات ومواقع أخبار ومنصات غنية بالمحتوى." },
      { title: "Corporate Presence", title_ar: "حضور مؤسسي", desc: "Professional corporate websites with CMS.", desc_ar: "مواقع مؤسسية احترافية مع إدارة محتوى." },
      { title: "Membership Sites", title_ar: "مواقع العضوية", desc: "Restricted content and subscription platforms.", desc_ar: "محتوى مقيد ومنصات اشتراك." },
    ],
    platforms: ["WordPress", "PHP", "MySQL"],
    seoTitle: "WordPress Development | Wavai",
    seoDesc: "Custom WordPress development. Themes, plugins, security, and performance optimization by Wavai.",
  },
  "corporate-websites": {
    title: "Corporate Websites",
    title_ar: "المواقع المؤسسية",
    heroDesc: "Professional websites that communicate your brand story, establish credibility, and drive business results.",
    heroDesc_ar: "مواقع احترافية تنقل قصة علامتك التجارية وتبني المصداقية وتحقق نتائج تجارية.",
    icon: Monitor,
    whatWeDo: "We create corporate websites that elevate your brand presence. From strategic messaging and visual identity to user experience and lead generation, every aspect is designed to position your company as a trusted industry leader.",
    whatWeDo_ar: "نبني مواقع مؤسسية ترتقي بحضور علامتك التجارية. من الرسائل الاستراتيجية والهوية البصرية إلى تجربة المستخدم وتوليد العملاء المحتملين.",
    features: [
      { icon: Lightbulb, title: "Brand Strategy", title_ar: "استراتيجية العلامة التجارية", desc: "Strategic messaging aligned with business goals.", desc_ar: "رسائل استراتيجية متوافقة مع أهداف الأعمال." },
      { icon: Palette, title: "Visual Identity", title_ar: "الهوية البصرية", desc: "Consistent design language across all pages.", desc_ar: "لغة تصميم متسقة عبر جميع الصفحات." },
      { icon: Users, title: "Lead Generation", title_ar: "توليد العملاء", desc: "Contact forms, CTAs, and conversion paths.", desc_ar: "نماذج اتصال ودعوات للعمل ومسارات تحويل." },
      { icon: Globe, title: "Multilingual", title_ar: "متعدد اللغات", desc: "Multi-language support for global audiences.", desc_ar: "دعم متعدد اللغات للجمهور العالمي." },
    ],
    useCases: [
      { title: "Brand Refresh", title_ar: "تحديث العلامة التجارية", desc: "Modernizing your company's digital presence.", desc_ar: "تحديث الحضور الرقمي لشركتك." },
      { title: "New Company", title_ar: "شركة جديدة", desc: "Establishing credibility with a professional website.", desc_ar: "بناء المصداقية مع موقع احترافي." },
    ],
    platforms: ["Webflow", "WordPress"],
    seoTitle: "Corporate Website Design | Wavai",
    seoDesc: "Professional corporate website design and development. Establish your brand's digital presence with Wavai.",
  },
  "landing-pages": {
    title: "Landing Pages",
    title_ar: "صفحات الهبوط",
    heroDesc: "High-converting landing pages designed for campaigns, product launches, and lead generation.",
    heroDesc_ar: "صفحات هبوط عالية التحويل مصممة للحملات وإطلاق المنتجات وتوليد العملاء المحتملين.",
    icon: PenTool,
    whatWeDo: "We design and build landing pages that convert. Whether it's a product launch, marketing campaign, or lead generation funnel, we create focused, persuasive pages with clear CTAs, compelling copy, and data-backed design decisions.",
    whatWeDo_ar: "نصمم ونبني صفحات هبوط تحقق تحويلات. سواء كان إطلاق منتج أو حملة تسويقية أو مسار توليد عملاء, نبني صفحات مركزة ومقنعة.",
    features: [
      { icon: Target, title: "Conversion Design", title_ar: "تصميم التحويل", desc: "Every element optimized for a single goal.", desc_ar: "كل عنصر محسّن لهدف واحد." },
      { icon: PenTool, title: "Compelling Copy", title_ar: "نصوص مقنعة", desc: "Persuasive messaging that drives action.", desc_ar: "رسائل مقنعة تدفع للعمل." },
      { icon: Gauge, title: "Fast Loading", title_ar: "تحميل سريع", desc: "Lightweight pages for instant load times.", desc_ar: "صفحات خفيفة لأوقات تحميل فورية." },
      { icon: BarChart3, title: "A/B Testing Ready", title_ar: "جاهزة لاختبارات A/B", desc: "Built for easy testing and optimization.", desc_ar: "مبنية لاختبار وتحسين سهل." },
    ],
    useCases: [
      { title: "Product Launch", title_ar: "إطلاق منتج", desc: "Creating buzz for a new product or service.", desc_ar: "إثارة الاهتمام بمنتج أو خدمة جديدة." },
      { title: "Ad Campaigns", title_ar: "حملات إعلانية", desc: "Landing pages for paid advertising campaigns.", desc_ar: "صفحات هبوط لحملات الإعلانات المدفوعة." },
    ],
    platforms: ["Webflow", "WordPress", "Shopify"],
    seoTitle: "Landing Page Design | Wavai",
    seoDesc: "High-converting landing page design for campaigns, launches, and lead generation by Wavai.",
  },
  "multilingual-websites": {
    title: "Multilingual Websites",
    title_ar: "المواقع متعددة اللغات",
    heroDesc: "Reach global audiences with fully localized, RTL-ready multilingual websites.",
    heroDesc_ar: "وصول إلى جمهور عالمي مع مواقع متعددة اللغات مترجمة بالكامل وجاهزة لـ RTL.",
    icon: Globe,
    whatWeDo: "We build websites that speak your customers' language, literally. From Arabic RTL support and automatic translation to hreflang tags and localized SEO, we create seamless multilingual experiences that drive engagement across markets.",
    whatWeDo_ar: "نبني مواقع تتحدث بلغة عملائك, حرفياً. من دعم RTL العربي والترجمة التلقائية إلى علامات hreflang وSEO المترجم.",
    features: [
      { icon: Globe, title: "RTL Support", title_ar: "دعم RTL", desc: "Full right-to-left layout for Arabic and Hebrew.", desc_ar: "تخطيط كامل من اليمين لليسار للعربية والعبرية." },
      { icon: Layers, title: "Translation Management", title_ar: "إدارة الترجمة", desc: "CMS-driven translation workflows.", desc_ar: "سير عمل ترجمة مدعومة بإدارة المحتوى." },
      { icon: Target, title: "Localized SEO", title_ar: "SEO مترجم", desc: "Hreflang tags and market-specific optimization.", desc_ar: "علامات hreflang وتحسين خاص بالسوق." },
      { icon: Settings, title: "Auto Language Detection", title_ar: "كشف اللغة التلقائي", desc: "Redirect users based on browser language.", desc_ar: "إعادة توجيه المستخدمين بناءً على لغة المتصفح." },
    ],
    useCases: [
      { title: "GCC Businesses", title_ar: "شركات الخليج", desc: "Need English and Arabic with proper RTL support.", desc_ar: "تحتاج إنجليزي وعربي مع دعم RTL صحيح." },
      { title: "International Expansion", title_ar: "توسع دولي", desc: "Launching in new markets with local language support.", desc_ar: "الإطلاق في أسواق جديدة مع دعم اللغة المحلية." },
    ],
    platforms: ["Webflow", "WordPress", "Weglot"],
    seoTitle: "Multilingual Website Development | Wavai",
    seoDesc: "Multilingual website development with RTL support. Localized websites for global audiences by Wavai.",
  },
  "cms-implementation": {
    title: "CMS Implementation",
    title_ar: "أنظمة إدارة المحتوى",
    heroDesc: "Structured content management setups that empower your team to update content independently.",
    heroDesc_ar: "إعداد إدارة محتوى منظمة تمكّن فريقك من تحديث المحتوى بشكل مستقل.",
    icon: Settings,
    whatWeDo: "We implement content management systems that give your team full control over website content without touching code. From CMS architecture and content modeling to editorial workflows and training, we make content management effortless.",
    whatWeDo_ar: "ننفذ أنظمة إدارة محتوى تمنح فريقك سيطرة كاملة على محتوى الموقع بدون لمس الكود.",
    features: [
      { icon: Layers, title: "Content Architecture", title_ar: "بنية المحتوى", desc: "Structured content models for organized management.", desc_ar: "نماذج محتوى منظمة لإدارة منظمة." },
      { icon: Users, title: "Editorial Workflows", title_ar: "سير العمل التحريري", desc: "Review, approval, and publishing workflows.", desc_ar: "سير عمل المراجعة والموافقة والنشر." },
      { icon: Settings, title: "Custom Fields", title_ar: "حقول مخصصة", desc: "Tailored input fields for your content types.", desc_ar: "حقول إدخال مخصصة لأنواع المحتوى الخاص بك." },
      { icon: BookOpen, title: "Team Training", title_ar: "تدريب الفريق", desc: "Hands-on training for your content team.", desc_ar: "تدريب عملي لفريق المحتوى الخاص بك." },
    ],
    useCases: [
      { title: "Marketing Teams", title_ar: "فرق التسويق", desc: "Need to update content without developer help.", desc_ar: "تحتاج لتحديث المحتوى بدون مساعدة المطورين." },
      { title: "Content-Heavy Sites", title_ar: "مواقع غنية بالمحتوى", desc: "Managing large volumes of structured content.", desc_ar: "إدارة كميات كبيرة من المحتوى المنظم." },
    ],
    platforms: ["Webflow CMS", "WordPress", "Sanity", "Contentful"],
    seoTitle: "CMS Implementation | Wavai",
    seoDesc: "CMS implementation and content management setup. Empower your team with structured content management.",
  },
  "website-redesign": {
    title: "Website Redesign & Optimization",
    title_ar: "إعادة تصميم وتحسين المواقع",
    heroDesc: "Modernize your existing website with improved design, faster performance, and better SEO.",
    heroDesc_ar: "حدّث موقعك الحالي بتصميم محسّن وأداء أسرع وSEO أفضل.",
    icon: Wrench,
    whatWeDo: "We transform outdated websites into modern, high-performing digital experiences. From visual redesign and UX improvements to speed optimization and SEO, we breathe new life into your web presence while preserving what works.",
    whatWeDo_ar: "نحوّل المواقع القديمة إلى تجارب رقمية حديثة وعالية الأداء. من إعادة التصميم البصري وتحسين تجربة المستخدم إلى تحسين السرعة وSEO.",
    features: [
      { icon: Palette, title: "Visual Refresh", title_ar: "تحديث بصري", desc: "Modern design aligned with current trends.", desc_ar: "تصميم حديث متوافق مع الاتجاهات الحالية." },
      { icon: Gauge, title: "Speed Optimization", title_ar: "تحسين السرعة", desc: "Faster load times and better Core Web Vitals.", desc_ar: "أوقات تحميل أسرع ومؤشرات ويب أساسية أفضل." },
      { icon: Target, title: "SEO Improvement", title_ar: "تحسين SEO", desc: "Technical SEO and content optimization.", desc_ar: "SEO تقني وتحسين المحتوى." },
      { icon: Monitor, title: "Mobile Responsiveness", title_ar: "استجابة الجوال", desc: "Ensure perfect display on all devices.", desc_ar: "ضمان عرض مثالي على جميع الأجهزة." },
    ],
    useCases: [
      { title: "Outdated Website", title_ar: "موقع قديم", desc: "Your website looks dated and needs a refresh.", desc_ar: "موقعك يبدو قديماً ويحتاج تحديثاً." },
      { title: "Slow Performance", title_ar: "أداء بطيء", desc: "Site speed is affecting SEO and user experience.", desc_ar: "سرعة الموقع تؤثر على SEO وتجربة المستخدم." },
    ],
    platforms: ["Webflow", "WordPress", "Shopify"],
    seoTitle: "Website Redesign & Optimization | Wavai",
    seoDesc: "Website redesign and optimization services. Modernize your site with better design, speed, and SEO.",
  },
  "ai-agents": {
    title: "AI Agents & Automation",
    title_ar: "وكلاء الذكاء الاصطناعي والأتمتة",
    heroDesc: "Build intelligent agents that handle customer interactions, data processing, and business operations autonomously.",
    heroDesc_ar: "بناء وكلاء ذكيين يتعاملون مع تفاعلات العملاء ومعالجة البيانات والعمليات التجارية بشكل مستقل.",
    icon: Bot,
    whatWeDo: "We design and deploy AI agents that automate complex business processes. From customer service automation and data extraction to intelligent decision-making workflows, our agents work 24/7, handling tasks that previously required human intervention.",
    whatWeDo_ar: "نصمم وننشر وكلاء ذكاء اصطناعي يؤتمتون عمليات الأعمال المعقدة. من أتمتة خدمة العملاء واستخراج البيانات إلى سير العمل الذكي.",
    features: [
      { icon: Bot, title: "Custom Agents", title_ar: "وكلاء مخصصون", desc: "Purpose-built AI agents for your specific workflows.", desc_ar: "وكلاء ذكاء اصطناعي مبنيون خصيصاً لسير عملك." },
      { icon: Settings, title: "Process Automation", title_ar: "أتمتة العمليات", desc: "Automate repetitive tasks and decision-making.", desc_ar: "أتمتة المهام المتكررة واتخاذ القرارات." },
      { icon: Puzzle, title: "Tool Integration", title_ar: "تكامل الأدوات", desc: "Connect agents with your existing business tools.", desc_ar: "ربط الوكلاء بأدوات عملك الحالية." },
      { icon: BarChart3, title: "Performance Monitoring", title_ar: "مراقبة الأداء", desc: "Track agent performance and accuracy metrics.", desc_ar: "تتبع أداء الوكيل ومقاييس الدقة." },
      { icon: Shield, title: "Safety & Guardrails", title_ar: "الأمان والحماية", desc: "Built-in safeguards for reliable AI operations.", desc_ar: "ضمانات مدمجة لعمليات ذكاء اصطناعي موثوقة." },
      { icon: TrendingUp, title: "Continuous Learning", title_ar: "التعلم المستمر", desc: "Agents that improve over time with your data.", desc_ar: "وكلاء يتحسنون بمرور الوقت مع بياناتك." },
    ],
    useCases: [
      { title: "Customer Support", title_ar: "دعم العملاء", desc: "Automate tier-1 support with AI agents.", desc_ar: "أتمتة الدعم من المستوى الأول بوكلاء ذكاء اصطناعي." },
      { title: "Data Processing", title_ar: "معالجة البيانات", desc: "Extract, transform, and analyze data automatically.", desc_ar: "استخراج وتحويل وتحليل البيانات تلقائياً." },
      { title: "Operations", title_ar: "العمليات", desc: "Streamline back-office operations with intelligent automation.", desc_ar: "تبسيط عمليات المكتب الخلفي بأتمتة ذكية." },
    ],
    platforms: ["OpenAI", "LangChain", "Make", "Zapier"],
    seoTitle: "AI Agents & Automation | Wavai",
    seoDesc: "Custom AI agents and automation solutions. Build intelligent agents for customer support, data processing, and operations.",
  },
  "ai-content-generation": {
    title: "AI Content Generation",
    title_ar: "إنشاء المحتوى بالذكاء الاصطناعي",
    heroDesc: "Generate brand-consistent blog posts, product descriptions, and marketing copy at scale.",
    heroDesc_ar: "إنشاء مقالات ووصف منتجات ونصوص تسويقية متسقة مع العلامة التجارية على نطاق واسع.",
    icon: Sparkles,
    whatWeDo: "We build AI-powered content generation systems tailored to your brand voice. From product descriptions and blog articles to social media posts and email campaigns, produce high-quality content at scale while maintaining brand consistency.",
    whatWeDo_ar: "نبني أنظمة إنشاء محتوى مدعومة بالذكاء الاصطناعي مصممة لصوت علامتك التجارية.",
    features: [
      { icon: Sparkles, title: "Brand Voice AI", title_ar: "صوت العلامة التجارية", desc: "AI trained on your brand guidelines and tone.", desc_ar: "ذكاء اصطناعي مدرب على إرشادات ونبرة علامتك التجارية." },
      { icon: BookOpen, title: "Blog Generation", title_ar: "إنشاء المدونات", desc: "SEO-optimized articles at scale.", desc_ar: "مقالات محسّنة لـ SEO على نطاق واسع." },
      { icon: Package, title: "Product Descriptions", title_ar: "وصف المنتجات", desc: "Compelling descriptions for entire catalogs.", desc_ar: "أوصاف مقنعة لكتالوجات كاملة." },
      { icon: Globe, title: "Multilingual Content", title_ar: "محتوى متعدد اللغات", desc: "Generate content in multiple languages.", desc_ar: "إنشاء محتوى بلغات متعددة." },
    ],
    useCases: [
      { title: "Ecommerce Catalogs", title_ar: "كتالوجات التجارة الإلكترونية", desc: "Need descriptions for hundreds of products.", desc_ar: "تحتاج أوصافاً لمئات المنتجات." },
      { title: "Content Marketing", title_ar: "التسويق بالمحتوى", desc: "Scaling content production for SEO and engagement.", desc_ar: "توسيع إنتاج المحتوى لـ SEO والتفاعل." },
    ],
    platforms: ["OpenAI", "Claude", "Custom LLMs"],
    seoTitle: "AI Content Generation | Wavai",
    seoDesc: "AI-powered content generation. Brand-consistent copy, blog posts, and product descriptions at scale.",
  },
  "chatbots-assistants": {
    title: "Chatbots & Assistants",
    title_ar: "روبوتات الدردشة والمساعدات الذكية",
    heroDesc: "Deploy smart chatbots that understand your business, answer questions, and guide customers to conversion.",
    heroDesc_ar: "نشر روبوتات دردشة ذكية تفهم عملك وتجيب على الأسئلة وتوجه العملاء للتحويل.",
    icon: MessageSquare,
    whatWeDo: "We build AI-powered chatbots and virtual assistants that provide instant, accurate responses to customer inquiries. Trained on your business data, these bots handle support, sales, and onboarding conversations naturally.",
    whatWeDo_ar: "نبني روبوتات دردشة ومساعدين افتراضيين مدعومين بالذكاء الاصطناعي يقدمون ردوداً فورية ودقيقة.",
    features: [
      { icon: MessageSquare, title: "Natural Language", title_ar: "لغة طبيعية", desc: "Conversations that feel human and helpful.", desc_ar: "محادثات تبدو بشرية ومفيدة." },
      { icon: Brain, title: "Business Knowledge", title_ar: "معرفة الأعمال", desc: "Trained on your products, policies, and FAQs.", desc_ar: "مدرب على منتجاتك وسياساتك والأسئلة الشائعة." },
      { icon: Puzzle, title: "Multi-Channel", title_ar: "متعدد القنوات", desc: "Deploy on website, WhatsApp, and social media.", desc_ar: "نشر على الموقع وWhatsApp ووسائل التواصل." },
      { icon: BarChart3, title: "Analytics", title_ar: "التحليلات", desc: "Track conversations and customer satisfaction.", desc_ar: "تتبع المحادثات ورضا العملاء." },
    ],
    useCases: [
      { title: "Customer Support", title_ar: "دعم العملاء", desc: "24/7 automated support for common questions.", desc_ar: "دعم آلي على مدار الساعة للأسئلة الشائعة." },
      { title: "Sales Assistance", title_ar: "مساعدة المبيعات", desc: "Guide visitors to the right products.", desc_ar: "توجيه الزوار للمنتجات المناسبة." },
    ],
    platforms: ["OpenAI", "WhatsApp", "Intercom"],
    seoTitle: "AI Chatbots & Assistants | Wavai",
    seoDesc: "AI-powered chatbots and virtual assistants. Smart customer support and sales automation by Wavai.",
  },
  "workflow-integrations": {
    title: "Workflow Integrations",
    title_ar: "تكامل سير العمل بالذكاء الاصطناعي",
    heroDesc: "Connect AI models to your existing tools, CRMs, ecommerce platforms, and productivity suites.",
    heroDesc_ar: "ربط نماذج الذكاء الاصطناعي بأدواتك الحالية, أنظمة إدارة العملاء ومنصات التجارة الإلكترونية.",
    icon: Settings,
    whatWeDo: "We integrate AI capabilities into your existing tech stack. From connecting GPT models to your CRM and ecommerce platform to building custom automation pipelines, we make AI work within your current workflows, not alongside them.",
    whatWeDo_ar: "ندمج قدرات الذكاء الاصطناعي في مجموعة التكنولوجيا الحالية. من ربط نماذج GPT بإدارة العملاء ومنصة التجارة الإلكترونية إلى بناء خطوط أتمتة مخصصة.",
    features: [
      { icon: Puzzle, title: "API Integrations", title_ar: "تكامل API", desc: "Connect AI with any tool via REST or GraphQL.", desc_ar: "ربط الذكاء الاصطناعي بأي أداة عبر REST أو GraphQL." },
      { icon: Settings, title: "Workflow Automation", title_ar: "أتمتة سير العمل", desc: "Automated pipelines triggered by business events.", desc_ar: "خطوط أتمتة تعمل بمحفزات الأعمال." },
      { icon: Layers, title: "Data Sync", title_ar: "مزامنة البيانات", desc: "Keep data consistent across all platforms.", desc_ar: "الحفاظ على اتساق البيانات عبر جميع المنصات." },
      { icon: Shield, title: "Error Handling", title_ar: "معالجة الأخطاء", desc: "Robust error handling and retry mechanisms.", desc_ar: "معالجة أخطاء متينة وآليات إعادة المحاولة." },
    ],
    useCases: [
      { title: "CRM Automation", title_ar: "أتمتة إدارة العملاء", desc: "AI-powered lead scoring and customer insights.", desc_ar: "تسجيل العملاء المحتملين ورؤى العملاء بالذكاء الاصطناعي." },
      { title: "Ecommerce AI", title_ar: "ذكاء اصطناعي للتجارة", desc: "Smart product recommendations and personalization.", desc_ar: "توصيات منتجات ذكية وتخصيص." },
    ],
    platforms: ["Make", "Zapier", "n8n", "Custom APIs"],
    seoTitle: "AI Workflow Integrations | Wavai",
    seoDesc: "AI workflow integrations. Connect AI models to your CRM, ecommerce, and business tools.",
  },
  "knowledge-base-automation": {
    title: "Knowledge Base Automation",
    title_ar: "أتمتة قواعد المعرفة",
    heroDesc: "Build AI-powered knowledge bases that learn from your content and serve instant, accurate answers.",
    heroDesc_ar: "بناء قواعد معرفة مدعومة بالذكاء الاصطناعي تتعلم من محتواك وتقدم إجابات فورية ودقيقة.",
    icon: BookOpen,
    whatWeDo: "We create intelligent knowledge base systems that automatically organize, index, and serve information from your documents, wikis, and internal resources. Your team and customers get instant answers without searching through endless documents.",
    whatWeDo_ar: "نبني أنظمة قواعد معرفة ذكية تنظم وتفهرس وتقدم المعلومات تلقائياً من وثائقك وويكيك ومواردك الداخلية.",
    features: [
      { icon: BookOpen, title: "Document Processing", title_ar: "معالجة المستندات", desc: "Automatically index and understand your documents.", desc_ar: "فهرسة وفهم مستنداتك تلقائياً." },
      { icon: Brain, title: "Semantic Search", title_ar: "بحث دلالي", desc: "Find answers based on meaning, not just keywords.", desc_ar: "إيجاد الإجابات بناءً على المعنى وليس الكلمات المفتاحية فقط." },
      { icon: MessageSquare, title: "Q&A Interface", title_ar: "واجهة أسئلة وأجوبة", desc: "Chat-based interface for instant knowledge retrieval.", desc_ar: "واجهة محادثة لاسترجاع المعرفة الفوري." },
      { icon: Settings, title: "Auto-Updates", title_ar: "تحديثات تلقائية", desc: "Knowledge base that stays current with your content.", desc_ar: "قاعدة معرفة تبقى محدثة مع محتواك." },
    ],
    useCases: [
      { title: "Internal Knowledge", title_ar: "المعرفة الداخلية", desc: "Help employees find information quickly.", desc_ar: "مساعدة الموظفين في إيجاد المعلومات بسرعة." },
      { title: "Customer Self-Service", title_ar: "الخدمة الذاتية للعملاء", desc: "Let customers find answers without contacting support.", desc_ar: "السماح للعملاء بإيجاد الإجابات بدون الاتصال بالدعم." },
    ],
    platforms: ["Pinecone", "OpenAI", "Notion", "Confluence"],
    seoTitle: "Knowledge Base Automation | Wavai",
    seoDesc: "AI-powered knowledge base automation. Instant answers from your documents and internal resources.",
  },
  "custom-ai-tools": {
    title: "Custom AI Tools & Dashboards",
    title_ar: "أدوات ولوحات معلومات ذكية مخصصة",
    heroDesc: "Internal tools and dashboards powered by AI, from data analysis to automated reporting and decision support.",
    heroDesc_ar: "أدوات داخلية ولوحات معلومات مدعومة بالذكاء الاصطناعي, من تحليل البيانات إلى التقارير الآلية ودعم القرار.",
    icon: Brain,
    whatWeDo: "We build custom AI-powered internal tools that give your team superpowers. From intelligent dashboards and automated reporting to data analysis tools and decision support systems, we create solutions that make your team more productive.",
    whatWeDo_ar: "نبني أدوات داخلية مخصصة مدعومة بالذكاء الاصطناعي تمنح فريقك قوى خارقة.",
    features: [
      { icon: BarChart3, title: "AI Dashboards", title_ar: "لوحات معلومات ذكية", desc: "Real-time insights with AI-powered analytics.", desc_ar: "رؤى في الوقت الفعلي مع تحليلات مدعومة بالذكاء الاصطناعي." },
      { icon: Brain, title: "Automated Reports", title_ar: "تقارير آلية", desc: "Generate comprehensive reports automatically.", desc_ar: "إنشاء تقارير شاملة تلقائياً." },
      { icon: Target, title: "Decision Support", title_ar: "دعم القرار", desc: "AI recommendations for business decisions.", desc_ar: "توصيات ذكاء اصطناعي للقرارات التجارية." },
      { icon: Code, title: "Custom Development", title_ar: "تطوير مخصص", desc: "Fully tailored tools for your specific needs.", desc_ar: "أدوات مخصصة بالكامل لاحتياجاتك المحددة." },
    ],
    useCases: [
      { title: "Executive Dashboards", title_ar: "لوحات معلومات تنفيذية", desc: "Real-time KPI tracking with AI insights.", desc_ar: "تتبع مؤشرات الأداء الرئيسية في الوقت الفعلي مع رؤى ذكية." },
      { title: "Operations Tools", title_ar: "أدوات العمليات", desc: "Custom tools to streamline daily operations.", desc_ar: "أدوات مخصصة لتبسيط العمليات اليومية." },
    ],
    platforms: ["React", "Python", "OpenAI", "Supabase"],
    seoTitle: "Custom AI Tools & Dashboards | Wavai",
    seoDesc: "Custom AI-powered tools and dashboards. Internal tools for data analysis, reporting, and decision support.",
  },
};

/* ─── Animations ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.06, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

/* ─── Component ─── */
export default function ServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { lang, t, localePath } = useLanguage();
  const isAr = lang === "ar";

  const service = slug ? SERVICES[slug] : null;

  const seoPath = `/services/${slug}`;
  const { canonical, hreflang } = buildHreflang(seoPath);
  useCmsPageSEO({
    title: service?.seoTitle || "Services | Wavai",
    description: service?.seoDesc || "",
    canonical,
    hreflang,
  }, seoPath);

  if (!service) return <Navigate to="/services" replace />;

  const Icon = service.icon;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-hero opacity-30" />
          <div className="absolute top-1/2 -translate-y-1/2 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="container px-6 relative z-10">
            <motion.div
              className="max-w-3xl mx-auto text-center"
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            >
              <motion.div className="flex justify-center mb-6" variants={fadeUp}>
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Icon className="h-7 w-7 text-primary" />
                </div>
              </motion.div>
              <motion.h1
                className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-6"
                variants={fadeUp}
              >
                {isAr ? service.title_ar : service.title}
              </motion.h1>
              <motion.p
                className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-8"
                variants={fadeUp}
              >
                {isAr ? service.heroDesc_ar : service.heroDesc}
              </motion.p>
              <motion.div variants={fadeUp}>
                <Button variant="hero" size="lg" asChild>
                  <Link to={localePath("/start-a-project")}>
                    {t("Start a Project", "ابدأ مشروعك")}
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* What We Do */}
        <section className="py-20 lg:py-28 bg-secondary/30">
          <div className="container px-6 max-w-3xl mx-auto text-center">
            <motion.h2
              className="text-3xl sm:text-4xl font-display font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {t("What We Do", "ماذا نقدم")}
            </motion.h2>
            <motion.p
              className="text-lg text-muted-foreground leading-relaxed"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {isAr ? service.whatWeDo_ar : service.whatWeDo}
            </motion.p>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 lg:py-28">
          <div className="container px-6">
            <motion.div
              className="text-center max-w-2xl mx-auto mb-14"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm uppercase tracking-[0.15em] text-primary font-medium mb-4">
                {t("Capabilities", "القدرات")}
              </p>
              <h2 className="text-3xl sm:text-4xl font-display font-bold">
                {t("Key ", "الميزات ")}<span className="text-gradient">{t("Features", "الرئيسية")}</span>
              </h2>
            </motion.div>

            <motion.div
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
            >
              {service.features.map((f, i) => {
                const FIcon = f.icon;
                return (
                  <motion.div
                    key={i}
                    className="group p-6 rounded-2xl bg-card border border-border/60 hover:border-primary/25 transition-all duration-300 hover:shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.12)] hover:-translate-y-0.5"
                    variants={fadeUp}
                    custom={i}
                  >
                    <div className="w-11 h-11 rounded-xl bg-primary/8 flex items-center justify-center mb-5 group-hover:bg-primary/15 transition-colors duration-300">
                      <FIcon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="text-base font-sans font-medium mb-2 group-hover:text-primary transition-colors duration-300">
                      {isAr ? f.title_ar : f.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {isAr ? f.desc_ar : f.desc}
                    </p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 lg:py-28 bg-secondary/30">
          <div className="container px-6">
            <motion.div
              className="text-center max-w-2xl mx-auto mb-14"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm uppercase tracking-[0.15em] text-primary font-medium mb-4">
                {t("When You Need This", "متى تحتاج هذا")}
              </p>
              <h2 className="text-3xl sm:text-4xl font-display font-bold">
                {t("Use ", "حالات ")}<span className="text-gradient">{t("Cases", "الاستخدام")}</span>
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {service.useCases.map((uc, i) => (
                <motion.div
                  key={i}
                  className="p-6 rounded-2xl bg-card border border-border/60"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  </div>
                  <h3 className="text-base font-sans font-medium mb-2">
                    {isAr ? uc.title_ar : uc.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {isAr ? uc.desc_ar : uc.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Platforms */}
        {service.platforms && service.platforms.length > 0 && (
          <section className="py-20 lg:py-28">
            <div className="container px-6">
              <motion.div
                className="text-center max-w-2xl mx-auto mb-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl sm:text-4xl font-display font-bold">
                  {t("Platforms & ", "المنصات و")}<span className="text-gradient">{t("Tools", "الأدوات")}</span>
                </h2>
              </motion.div>
              <div className="flex flex-wrap justify-center gap-3 max-w-2xl mx-auto">
                {service.platforms.map((p) => (
                  <span
                    key={p}
                    className="px-5 py-2.5 rounded-full bg-secondary border border-border text-sm font-medium text-foreground"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-24 lg:py-32 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-hero opacity-20" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
          <div className="container px-6 relative z-10">
            <motion.div
              className="max-w-2xl mx-auto text-center"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            >
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold leading-[1.1] mb-5"
                variants={fadeUp}
              >
                {t("Ready to", "هل أنت مستعد")}{" "}
                <span className="text-gradient">{t("Get Started?", "للبدء؟")}</span>
              </motion.h2>
              <motion.p
                className="text-lg text-muted-foreground mb-10 max-w-lg mx-auto leading-relaxed"
                variants={fadeUp}
              >
                {t(
                  "Tell us about your project and we'll craft a tailored plan to bring your vision to life.",
                  "أخبرنا عن مشروعك وسنضع خطة مخصصة لتحقيق رؤيتك."
                )}
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
                variants={fadeUp}
              >
                <Button asChild size="lg" className="min-w-[180px]">
                  <Link to={localePath("/start-a-project")}>
                    {t("Start a Project", "ابدأ مشروعك")}
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="min-w-[180px]">
                  <Link to={localePath("/work")}>
                    {t("View Our Work", "شاهد أعمالنا")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
